/**
 * @file 一些用来辅助开发者 debug 的 hooks
 */
import { useWhyDidYouUpdate as _useWhyDidYouUpdate } from 'ahooks';
import { useRef, useEffect } from 'react';
/**
 * 帮助开发者排查是什么改变导致了组件的 rerender
 *
 * @category Debugger Hook
 */
export const useWhyDidYouUpdate = _useWhyDidYouUpdate;
const usePrevious = (value, initialValue) => {
    const ref = useRef(initialValue);
    useEffect(() => {
        ref.current = value;
    });
    return ref.current;
};
/**
 * 替换代码中的 useEffect 为 useEffectDebugger 即可打印是哪个依赖触发了 useEffect
 *
 * @category Debugger Hook
 * @param effectHook - 传给 useEffect 的 callback, 无需修改
 * @param dependencies - 传给 useEffect 的依赖列表, 无需修改
 * @param dependencyNames - 对依赖进行命名, 以增强打印信息的可读性
 */
export const useEffectDebugger = (effectHook, dependencies, dependencyNames) => {
    const previousDeps = usePrevious(dependencies, []);
    const changedDeps = dependencies.reduce((accum, dependency, index) => {
        if (dependency !== previousDeps[index]) {
            const keyName = dependencyNames[index];
            return Object.assign(Object.assign({}, accum), { [keyName]: {
                    before: previousDeps[index],
                    after: dependency,
                } });
        }
        return accum;
    }, {});
    if (Object.keys(changedDeps).length) {
        // eslint-disable-next-line no-console
        console.log('[useEffectDebugger] ', changedDeps);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(effectHook, dependencies);
};
//# sourceMappingURL=data:application/json;base64,eyJ2ZXJzaW9uIjozLCJmaWxlIjoiZGVidWdnZXJzLmpzIiwic291cmNlUm9vdCI6IiIsInNvdXJjZXMiOlsiLi4vLi4vLi4vc3JjL2hvb2tzL2RlYnVnZ2Vycy50cyJdLCJuYW1lcyI6W10sIm1hcHBpbmdzIjoiQUFBQTs7R0FFRztBQUVILE9BQU8sRUFBRSxrQkFBa0IsSUFBSSxtQkFBbUIsRUFBRSxNQUFNLFFBQVEsQ0FBQTtBQUNsRSxPQUFPLEVBQUUsTUFBTSxFQUFFLFNBQVMsRUFBa0MsTUFBTSxPQUFPLENBQUE7QUFFekU7Ozs7R0FJRztBQUNILE1BQU0sQ0FBQyxNQUFNLGtCQUFrQixHQUFHLG1CQUFtQixDQUFBO0FBRXJELE1BQU0sV0FBVyxHQUFHLENBQUMsS0FBVSxFQUFFLFlBQWlCLEVBQUUsRUFBRTtJQUNwRCxNQUFNLEdBQUcsR0FBRyxNQUFNLENBQUMsWUFBWSxDQUFDLENBQUE7SUFFaEMsU0FBUyxDQUFDLEdBQUcsRUFBRTtRQUNiLEdBQUcsQ0FBQyxPQUFPLEdBQUcsS0FBSyxDQUFBO0lBQ3JCLENBQUMsQ0FBQyxDQUFBO0lBRUYsT0FBTyxHQUFHLENBQUMsT0FBTyxDQUFBO0FBQ3BCLENBQUMsQ0FBQTtBQUVEOzs7Ozs7O0dBT0c7QUFDSCxNQUFNLENBQUMsTUFBTSxpQkFBaUIsR0FBRyxDQUMvQixVQUEwQixFQUMxQixZQUE0QixFQUM1QixlQUF5QixFQUN6QixFQUFFO0lBQ0YsTUFBTSxZQUFZLEdBQUcsV0FBVyxDQUFDLFlBQVksRUFBRSxFQUFFLENBQUMsQ0FBQTtJQUVsRCxNQUFNLFdBQVcsR0FBRyxZQUFZLENBQUMsTUFBTSxDQUFDLENBQUMsS0FBSyxFQUFFLFVBQVUsRUFBRSxLQUFLLEVBQUUsRUFBRTtRQUNuRSxJQUFJLFVBQVUsS0FBSyxZQUFZLENBQUMsS0FBSyxDQUFDLEVBQUU7WUFDdEMsTUFBTSxPQUFPLEdBQUcsZUFBZSxDQUFDLEtBQUssQ0FBQyxDQUFBO1lBQ3RDLHVDQUNLLEtBQUssS0FDUixDQUFDLE9BQU8sQ0FBQyxFQUFFO29CQUNULE1BQU0sRUFBRSxZQUFZLENBQUMsS0FBSyxDQUFDO29CQUMzQixLQUFLLEVBQUUsVUFBVTtpQkFDbEIsSUFDRjtTQUNGO1FBRUQsT0FBTyxLQUFLLENBQUE7SUFDZCxDQUFDLEVBQUUsRUFBRSxDQUFDLENBQUE7SUFFTixJQUFJLE1BQU0sQ0FBQyxJQUFJLENBQUMsV0FBVyxDQUFDLENBQUMsTUFBTSxFQUFFO1FBQ25DLHNDQUFzQztRQUN0QyxPQUFPLENBQUMsR0FBRyxDQUFDLHNCQUFzQixFQUFFLFdBQVcsQ0FBQyxDQUFBO0tBQ2pEO0lBRUQsdURBQXVEO0lBQ3ZELFNBQVMsQ0FBQyxVQUFVLEVBQUUsWUFBWSxDQUFDLENBQUE7QUFDckMsQ0FBQyxDQUFBIn0=